import { useEffect, useState } from 'react';
import { Grid2, Button, ButtonGroup } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { Field, Form } from 'react-final-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { Title, BasicTable, SearchBox, PaginationControlled, CircularIndeterminate, TextInput, Chip } from '@utriper/core-ui';
import { ERRORS, validateEmail, UserStatus } from '@utriper/core';

import Layout from 'components/Layout';

import { useUser } from 'core/contexts/user';

import { useStyles } from './styles';

const EmailForm = ({ user, onSubmit }) => {
    const [isEditable, setIsEditable] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);

    useEffect(() => {
        if (userToEdit?.id === user?.id) {
            setIsEditable(true);
        } else {
            setIsEditable(false);
        }
    }, [userToEdit, user?.id]);

    return (
        <Form
            key={user?.id || userToEdit?.id || 'new'}
            onSubmit={onSubmit}
            initialValues={userToEdit}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                    <Grid2 container spacing={1} alignItems="stretch">
                        <Grid2
                            onClick={() => {
                                if (!isEditable || !userToEdit?.id) {
                                    setUserToEdit(user);
                                    setIsEditable(true);
                                }
                            }}>
                            <Field id="email" name="email" component={TextInput} isEditable={isEditable} displayValue={user?.email} />
                        </Grid2>

                        {isEditable && (
                            <Grid2>
                                <ButtonGroup aria-label="Basic button group">
                                    <Button
                                        type="submit"
                                        variant="outlined"
                                        sx={{
                                            height: '51.5px', // Misma altura que el input :)
                                            minWidth: '36px',
                                        }}
                                        onClick={(e) => {
                                            handleSubmit(e);
                                            setIsEditable(false);
                                            setUserToEdit(null);
                                        }}>
                                        <Check />
                                    </Button>
                                    <Button
                                        type="cancel"
                                        variant="outlined"
                                        sx={{
                                            height: '51.5px', // Misma altura que el input :)
                                            minWidth: '36px',
                                        }}
                                        onClick={(e) => {
                                            setIsEditable(false);
                                            setUserToEdit(null);
                                        }}>
                                        <Close />
                                    </Button>
                                </ButtonGroup>
                            </Grid2>
                        )}
                    </Grid2>
                </form>
            )}
        />
    );
};

const UsersPage = () => {
    const classes = useStyles();
    const { t: translate, i18n } = useTranslation();

    const { getUsers, update, unlock, users, loading, total } = useUser();

    const [emailSearch, setEmailSearch] = useState('');
    const [page, setPage] = useState(1);
    const MAX_PAGE = 20;

    useEffect(() => {
        getUsers({
            emailSearch,
            pager: {
                page,
                pageSize: MAX_PAGE,
            },
        });
    }, []);

    useEffect(() => {
        getUsers({
            emailSearch,
            pager: {
                page,
                pageSize: MAX_PAGE,
            },
        });
    }, [page, emailSearch]);

    const columns = [
        { align: 'left', val: 'id', label: 'ID' },
        { val: 'email', label: 'Email' },
        { val: 'createdOn', label: 'Created On' },
        { val: 'lastLogin', label: 'Last Login' },
        { val: 'lastPasswordReset', label: 'Last Password Reset' },
        { align: 'center', val: 'status', label: 'Status', style: { width: '100%' } },
    ];
    const statusLabel = (status) => {
        if (status === UserStatus.IDS.active) return 'Active';
        if (status === UserStatus.IDS.blocked) return 'Locked';
        return 'Inactive';
    };
    const prettifyUsers = (users = []) => {
        return users.map((user) => ({
            ...user,
            email: <EmailForm user={user} onSubmit={onSubmitUpdate} />,
            status:
                user.status === UserStatus.IDS.blocked ? (
                    <Grid2 container direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <Chip color="error" label={statusLabel(user.status)} />
                        <Button variant="outlined" color="black" size="small" onClick={() => onUnlock(user)}>
                            Unlock
                        </Button>
                    </Grid2>
                ) : (
                    <Chip label={statusLabel(user.status)} color={user.status === UserStatus.IDS.active ? 'success' : 'error'} />
                ),
        }));
    };
    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const onChangeEmailSearch = (event) => {
        setEmailSearch(event.target.value);
    };

    const onSubmitUpdate = async (values) => {
        try {
            if (!validateEmail(values.email)) {
                toast.error(ERRORS.INVALID_EMAIL?.msg, {
                    position: 'top-right',
                    theme: 'colored',
                });
                return;
            }
            const result = await update(values);

            if (!result) {
                throw new Error(translate('utils.error_update'));
            }

            if (result) {
                getUsers({
                    emailSearch,
                    pager: {
                        page,
                        pageSize: MAX_PAGE,
                    },
                });
            }
        } catch (error) {
            toast.error(error?.message || error, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    const onUnlock = async (user) => {
        try {
            if (!window.confirm(`Unlock ${user.email}? This reactivates the account and resets the failed-attempt counter.`)) {
                return;
            }
            const result = await unlock(user.id);

            if (!result) {
                throw new Error(translate('utils.error_update'));
            }

            toast.success('Account unlocked', {
                position: 'top-right',
                theme: 'colored',
            });

            getUsers({
                emailSearch,
                pager: {
                    page,
                    pageSize: MAX_PAGE,
                },
            });
        } catch (error) {
            toast.error(error?.message || error, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    return (
        <Layout contentClassName={classes.content}>
            <Title value={'Users'} />
            <div style={{ marginBottom: '25px' }}>
                <Grid2 container direction="row" justifyContent="space-between" alignItems="center">
                    <SearchBox onChange={onChangeEmailSearch} value={emailSearch} label={'Email'} style={{ width: '14rem', marginRight: '1rem' }} />
                </Grid2>
            </div>
            <div>
                {loading ? (
                    <CircularIndeterminate />
                ) : (
                    <>
                        <BasicTable columns={columns} values={prettifyUsers(users)} />
                        <br />
                        <Grid2 container alignItems="flex-end">
                            <Grid2 size={{ xs: 1, sm: 7 }}></Grid2>
                            <Grid2 size={{ xs: 11, sm: 5 }}>{total / MAX_PAGE < 1 ? null : <PaginationControlled count={Math.ceil(total / MAX_PAGE)} page={page} onChange={handlePageChange} totalItems={total} itemsPerPage={MAX_PAGE} />}</Grid2>
                        </Grid2>
                    </>
                )}
            </div>
        </Layout>
    );
};

UsersPage.displayName = 'UsersPage';

export default UsersPage;
